import { Link } from "react-router-dom";

import { cn } from "@/shared/lib/utils";

import { ACQ_PRODUCT_NONE } from "../../api/acqService";
import { ALL_PRODUCTS } from "../../model/useProductParam";

/** 页签上的一格。`count` 为 null = 这一页没有可报的数(**不报 0**)。 */
export interface ProductTabItem {
  code: string;
  name: string;
  count?: number | null;
}

/**
 * 产品页签 —— 四页同一根主轴(契约 §16.2 / §16.7)。
 *
 * ★★ 老板原话:「我说的商机根据产品来现在做好了么,我没有感觉明显的操作方式」。
 *   上一版产品只是卡上的一个 chip 和筛子里的一档 —— **没有以产品为入口的操作路径**。
 *   这一排页签就是那条路径:选中一个产品,四页说的都是这个产品的事。
 * ★ 顺序**照给进来的顺序**(后端按产品树排),前端不按名字或数字重排 ——
 *   重排的后果是同一个租户在四页上看到四种排列,而他记的是位置不是名字。
 * ★ 数**没有就不摆**(`count == null`):报一个猜出来的 0,用户会照着它判断"这条产品线没单子"。
 * ★★ 「未指定产品」那一行的 `code` 是**空串**(契约逐字,它是显示值),而空串发给后端
 *   = 不筛 = 全部卡。所以这一排**做一次映射**:它的页签值是哨兵 `__none__`
 *   (契约 §16.2 api 落地形状:`/today` 与 `/opportunities` 两口都认它)。
 * ★ `allowNone=false` 的页面(去哪里找)上它**不是一档可选的东西** ——
 *   「为没绑产品的卡去发现一次」不成立(没有它的词、没有它的场景)。那儿它退成
 *   末尾一句话,数照报、点不动。
 * ★ 一个产品都没有 ⇒ **整排不渲染**(实测 33 个租户产品树全空):一排只有「全部」的
 *   页签是纯噪音,而"你还没建产品树"这件事该由页面自己的空态去说。
 * ★★ **不用 `role="tab"` / `role="tablist"`,用 `role="group"` + `aria-pressed`**
 *   (与今日页那个「全部 / 只看我的」分段控件同一套)。真渲染抓到的:
 *   `src/styles/skin-standard.css` 对 `[role="tab"]:hover` 写了
 *   `color: hsl(var(--stripe-blurple-deep)) !important` —— 鼠标停在**选中**那一格上时,
 *   深紫的字压在紫底上**整个读不出来**;而 `[role="tab"][data-state="active"]` 那条
 *   (`background: var(--card) !important`)又会把选中态的底色整个抹掉。
 *   那套皮肤规则是给 Radix Tabs(带 `data-state`)写的,这一排不是 Radix 的。
 *   ★ 三套皮肤各有一份这样的规则,所以这不是换个颜色能绕过去的 —— 得躲开那个 role。
 */
export function ProductTabs({
  items,
  value,
  onChange,
  allLabel = "全部",
  countLabel,
  allowNone = false,
  className,
}: {
  items: ProductTabItem[];
  value: string;
  onChange: (code: string) => void;
  /** 第一格的字。今日页是「全部」,别的页也是 —— 留个口子是为了"全部产品"这种说法。 */
  allLabel?: string;
  /** 数的单位,进 `aria-label` 与 `title`(「12 条在手」/「3 条今天」)。 */
  countLabel?: string;
  /**
   * 「未指定产品」能不能当一档点(**筛卡的页面给 true**:今日 / 线索库)。
   * 去哪里找给 false —— 那一页选产品是"为哪个产品去找",而"为没绑产品的卡去找"不成立。
   */
  allowNone?: boolean;
  className?: string;
}) {
  const tabs = items.filter((i) => i.code !== "");
  const unbound = items.find((i) => i.code === "");
  if (tabs.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      <div
        className="inline-flex flex-wrap gap-1 rounded-lg border p-0.5"
        role="group"
        aria-label="按产品看"
      >
        <TabButton
          label={allLabel}
          active={value === ALL_PRODUCTS}
          onClick={() => onChange(ALL_PRODUCTS)}
        />
        {tabs.map((t) => (
          <TabButton
            key={t.code}
            label={t.name}
            count={t.count}
            countLabel={countLabel}
            active={value === t.code}
            onClick={() => onChange(t.code)}
          />
        ))}
        {/* ★ 「未指定产品」排在最后:它不是一条产品线,是"剩下的那些"。
            ★★ 送出去的是哨兵 `__none__` 而不是它自己的空串 —— 见上面那条注释。 */}
        {allowNone && unbound != null && (
          <TabButton
            label={unbound.name}
            count={unbound.count}
            countLabel={countLabel}
            active={value === ACQ_PRODUCT_NONE}
            onClick={() => onChange(ACQ_PRODUCT_NONE)}
          />
        )}
      </div>
      {/* ★ 去哪里找那一页(`allowNone=false`):未绑定那一堆**不是一档能去找的产品**
          （它没有词、没有场景),所以不做页签。0 条时整句不说:一句「另有 0 条没绑产品」
          是在提醒一个不存在的问题。
          ★★ 0918 批次六(契约 §17.3.2):这句话**可点** —— 跳线索库的 `__none__` 筛。
          上一版它是一句灰字,用户读完"另有 7 条没绑产品"之后没有任何下一步:
          他得自己想到去线索库、再自己找到那个筛子。报了一个问题却不给去处,
          比不报更让人烦躁。 */}
      {!allowNone && unbound != null && typeof unbound.count === "number" && unbound.count > 0 && (
        <Link
          to={`/ai-huoke/leads?product=${ACQ_PRODUCT_NONE}`}
          className="text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-primary"
          title="这些卡还没绑上产品；点开去线索库单独看它们"
        >
          另有 {unbound.count} 条没绑产品
        </Link>
      )}
    </div>
  );
}

function TabButton({
  label,
  count,
  countLabel,
  active,
  onClick,
}: {
  label: string;
  count?: number | null;
  countLabel?: string;
  active: boolean;
  onClick: () => void;
}) {
  const hasCount = typeof count === "number";
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={hasCount && countLabel ? `${label}：${count} ${countLabel}` : label}
      className={cn(
        "max-w-[12rem] truncate rounded-md px-2.5 py-1 text-xs font-semibold transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      {label}
      {hasCount && (
        <span className={cn("ml-1 font-normal", active ? "opacity-80" : "opacity-70")}>
          {count}
        </span>
      )}
    </button>
  );
}
